import { makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { HeaderHeight } from "@ms/planner-generic-controls/lib/local/constants/StyleConstants";
import { flexChildSizeFlexible, flexParent, textClip } from "@ms/planner-generic-controls/lib/local/utilities/Mixin.styles";
const MainDialogWidth = "min(1024px, calc(100vw - 64px))";
const MainDialogHeight = "min(840px, calc(100vh - 64px))";
const MainDialogMaxWidth = "1024px";
const MainDialogMaxHeight = "840px";
const MainPanelWithSidePanelWidth = "704px"; // 1024px - 320px (Full width - side panel width)
const SidePanelWidth = "320px";
const MainPanelMinWidth = "300px";
const SidePanelMinWidth = "200px";
// Panel (sidecar) mode constants
/** Breakpoint below which panel mode falls back to dialog */ export const TaskEditorPanelDialogFallbackBreakpoint = 1024;
/** Panel width as a fraction of the primary view width */ export const TaskEditorPanelWidthRatio = 0.4;
/** Maximum panel width in pixels */ export const TaskEditorPanelMaxWidth = 704;
/** Fixed panel width in panel mode (sidecar) */ export const TaskEditorChatPanelWidth = 320;
/**
 * Styles for the TaskEditorContent component (supports both dialog and panel render modes)
 */ export const useTaskEditorContentStyles = makeStyles({
    // Overrides BaseDialog surface styles
    surface: {
        padding: "0",
        maxHeight: MainDialogMaxHeight,
        maxWidth: MainDialogMaxWidth,
        height: MainDialogHeight,
        width: MainDialogWidth,
        borderRadius: tokens.borderRadiusXLarge
    },
    body: {
        ...flexParent("column"),
        ...flexChildSizeFlexible("auto"),
        borderRadius: "inherit",
        padding: "0",
        overflow: "auto",
        height: "100%",
        width: "100%"
    },
    taskDialogContainer: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "auto 1fr",
        height: "100%",
        width: "100%"
    },
    contentArea: {
        display: "grid",
        gridTemplateColumns: "1fr auto",
        overflow: "auto",
        minWidth: 0,
        minHeight: 0
    },
    mainPanel: {
        ...flexParent("column"),
        minWidth: MainPanelMinWidth,
        boxSizing: "border-box",
        overflowY: "auto",
        scrollbarGutter: "stable"
    },
    mainPanelWithSidePanel: {
        ...flexChildSizeFlexible(MainPanelWithSidePanelWidth),
        minWidth: MainPanelMinWidth
    },
    header: {
        ...flexParent("row"),
        alignItems: "center",
        justifyContent: "space-between",
        minWidth: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        position: "sticky",
        top: 0,
        zIndex: 1,
        padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL} ${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
        borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
        "@media (max-width: 480px)": {
            padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`
        }
    },
    headerTitle: {
        ...flexChildSizeFlexible("auto"),
        ...textClip("ellipsis"),
        minWidth: 0,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
        lineHeight: tokens.lineHeightBase300,
        color: tokens.colorNeutralForeground1,
        cursor: "pointer"
    },
    topButtonContainer: {
        ...flexParent("row"),
        flexShrink: 0,
        alignItems: "center",
        gap: tokens.spacingHorizontalS
    },
    topButtons: {
        minWidth: "32px",
        padding: tokens.spacingHorizontalSNudge
    },
    breadcrumbsSection: {
        ...flexParent("row"),
        alignItems: "center"
    },
    breadcrumbItem: {
        maxWidth: "inherit",
        minWidth: 0,
        ...textClip("ellipsis")
    },
    breadcrumbButton: {
        maxWidth: "inherit",
        minWidth: 0,
        ...shorthands.borderColor("transparent"),
        color: tokens.colorBrandForegroundLink,
        ":hover": {
            color: tokens.colorBrandForegroundLinkHover,
            backgroundColor: "transparent"
        },
        ":active": {
            color: tokens.colorBrandForegroundLinkPressed,
            backgroundColor: "transparent"
        },
        "@media (forced-colors: active)": {
            ":hover": {
                color: "Highlight",
                backgroundColor: "HighlightText"
            }
        }
    },
    breadcrumbButtonContent: {
        ...textClip("ellipsis"),
        display: "block"
    },
    unclickableBreadcrumb: {
        ...textClip("ellipsis"),
        maxWidth: "inherit",
        minWidth: 0,
        cursor: "default"
    },
    moreOptionsMenu: {
        minWidth: "240px"
    },
    content: {
        ...flexChildSizeFlexible("auto"),
        minHeight: "300px",
        paddingTop: tokens.spacingVerticalM
    },
    sidePanel: {
        width: SidePanelWidth,
        minWidth: SidePanelMinWidth,
        maxWidth: SidePanelWidth
    },
    /** Side panel override for panel (sidecar) mode — fixed width, no shrink */ sidePanelInPanelMode: {
        flex: `0 0 ${SidePanelWidth}`,
        height: "100%"
    },
    // ─── Panel (sidecar) mode styles ───
    /** Root container when rendered as a side panel instead of a dialog */ panelRoot: {
        ...flexParent("column"),
        height: "100%",
        backgroundColor: tokens.colorNeutralBackground1,
        borderLeftWidth: "1px",
        borderLeftStyle: "solid",
        borderLeftColor: tokens.colorNeutralStroke2,
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
        flexShrink: 0
    },
    /** Row below the shared header in panel mode: editor pane + optional side panel */ panelContentArea: {
        ...flexParent("row"),
        flexGrow: 1,
        minHeight: 0,
        overflow: "hidden"
    },
    panelEditorPane: {
        ...flexParent("column"),
        ...flexChildSizeFlexible("0px"),
        height: "100%",
        overflowY: "auto",
        paddingTop: tokens.spacingVerticalM,
        boxSizing: "border-box"
    },
    /** Header override for panel mode (tighter padding) */ panelHeader: {
        padding: `${tokens.spacingVerticalNone} ${tokens.spacingHorizontalL}`,
        minHeight: HeaderHeight,
        flexShrink: 0
    },
    /** Flipped icon for the "switch to dialog" button in panel mode */ flippedIcon: {
        transform: "scaleX(-1)"
    },
    /** Ensures chat icon uses system colors in Windows High Contrast themes (Aquatic / Desert). */ chatButton: {
        "@media (forced-colors: active)": {
            color: "ButtonText",
            backgroundColor: "transparent"
        }
    }
});
